import { HttpError } from '@/errors/HttpError';
import { DEFAULT_HASHTAG_MONITORING_NETWORK_CODES } from '@/constants/hashtagPlatforms';
import type { HashtagPlatformRow } from '@/types/hashtag.types';

const FACEBOOK_ALIASES = new Set(['facebook', 'fb']);

const HASHTAG_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** BFF `platforms[].id` path param (UUID), not hashtag text ni campaign code. */
export const isHashtagRowUuid = (value: string): boolean =>
  HASHTAG_UUID_RE.test(String(value ?? '').trim());

export const platformRowId = (row: HashtagPlatformRow): string => row.id;

export const normalizeHashtagPlatformCode = (code: string): string => code.trim().toLowerCase();

export const isFacebookPlatform = (platformCode: string): boolean =>
  FACEBOOK_ALIASES.has(normalizeHashtagPlatformCode(platformCode));

export const isInstagramPlatform = (platformCode: string): boolean =>
  normalizeHashtagPlatformCode(platformCode) === 'instagram';

const MONITORING_ALIASES: Record<string, readonly string[]> = {
  x: ['x', 'twitter'],
  twitter: ['x', 'twitter'],
};

export const isScrapeAllExceptFacebookPlatform = (platformCode: string): boolean => {
  const normalized = normalizeHashtagPlatformCode(platformCode);
  if (isFacebookPlatform(normalized)) return false;
  return (DEFAULT_HASHTAG_MONITORING_NETWORK_CODES as readonly string[]).some(target => {
    const aliases = MONITORING_ALIASES[target] ?? [target];
    return aliases.includes(normalized);
  });
};

/** UUIDs de filas activas para scrape unitario (redes monitorizadas, sin Facebook). */
export const hashtagIdsForScrapeAllExceptFacebook = (platforms: HashtagPlatformRow[]): string[] =>
  platforms
    .filter(row => row.is_active !== false && isScrapeAllExceptFacebookPlatform(row.platform_code))
    .map(platformRowId);

const readBffErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') return null;

  const record = data as Record<string, unknown>;
  const detail = record.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();

  const message = record.message;
  if (typeof message === 'string' && message.trim()) return message.trim();

  if (Array.isArray(detail)) {
    const joined = detail
      .map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: string }).msg);
        }
        return '';
      })
      .filter(Boolean)
      .join(' ');
    return joined || null;
  }

  return null;
};

export const parseHashtagApiError = (err: unknown, fallback: string): string => {
  if (err instanceof HttpError) {
    const fromBody = readBffErrorMessage(err.response?.data);
    if (fromBody) return fromBody;
  }
  return fallback;
};
